import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from '@/services/neonFirestore'
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'
import { db } from '@/firebase/config'
import { apiGet } from '@/services/apiClient'
import { useAuthStore } from './auth'
import {
  filterBySubCompanyScope,
  filterBranchesForUser,
  filterEmployeesForUser,
  applySubCompanyMetadata,
  isBranchScopedManager,
  isBranchAssignableRole,
  getManagedBranchIds,
  normalizeManagedBranchIds,
  canManageEmployeeRecord,
  canManageBranch,
} from '@/utils/subCompanyScope'

/** Coerce Firestore Timestamp, Date, or ISO string to a JS Date. */
const toJsDate = (value) => {
  if (!value) return null
  if (typeof value.toDate === 'function') return value.toDate()
  if (value instanceof Date) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  return null
}

export const useCompanyStore = defineStore('company', () => {
  const companies = ref([])
  const employees = ref([])
  const rosterDirectory = ref([])
  const branches = ref([])
  const customRoles = ref([])
  const rolesLoaded = ref(false)
  const loading = ref(false)
  const authStore = useAuthStore()
  const getBusinessOwnerSubCompanyId = () => {
    const role = (authStore.userProfile?.role || '').toString().toLowerCase()
    if (role !== 'business_owner') return null
    return authStore.userProfile?.subCompanyId || null
  }

  // Load custom roles for current company
  const loadCustomRoles = async () => {
    // Wait for auth to be initialized if it hasn't been
    if (authStore.authLoading) {
      console.log('⏳ Waiting for auth to initialize before loading roles...')
      return
    }

    if (!authStore.userProfile?.companyId && !authStore.isSuperAdmin) {
      console.warn('Cannot load custom roles: No companyId in user profile and user is not superadmin')
      return
    }

    loading.value = true
    try {
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      let rolesQuery;
      if (authStore.isSuperAdmin && !authStore.userProfile?.companyId) {
        // Superadmin without companyId loads all roles
        rolesQuery = query(collection(db, 'roles'), orderBy('name', 'asc'))
      } else if (businessOwnerSubCompanyId) {
        rolesQuery = query(
          collection(db, 'roles'),
          where('companyId', '==', authStore.userProfile.companyId),
          where('subCompanyId', '==', businessOwnerSubCompanyId),
          orderBy('name', 'asc')
        )
      } else {
        rolesQuery = query(
          collection(db, 'roles'),
          where('companyId', '==', authStore.userProfile.companyId),
          orderBy('name', 'asc')
        )
      }

      const rolesSnapshot = await getDocs(rolesQuery)
      const loadedRoles = rolesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      }))
      customRoles.value = loadedRoles
      authStore.setCustomRoles(loadedRoles)
      rolesLoaded.value = true
    } catch (error) {
      console.error('Error loading custom roles:', error)
      if (error.code === 'permission-denied') {
        console.error('Permission denied loading roles. User:', {
          uid: authStore.userProfile?.uid,
          role: authStore.userProfile?.role,
          companyId: authStore.userProfile?.companyId
        })
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  // Create custom role
  const createCustomRole = async (roleData) => {
    if (!authStore.userProfile?.companyId) throw new Error('Company ID not found')

    loading.value = true
    try {
      const newRole = applySubCompanyMetadata({
        companyId: authStore.userProfile.companyId,
        name: roleData.name,
        permissions: roleData.permissions || [],
        pageAccess: roleData.pageAccess || null,
        isManagement: roleData.isManagement || false,
        description: roleData.description || '',
        overrides: roleData.overrides || null,
        createdAt: new Date(),
        updatedAt: new Date(),
      }, authStore.userProfile)

      const docRef = await addDoc(collection(db, 'roles'), {
        ...newRole,
        createdAt: Timestamp.fromDate(newRole.createdAt),
        updatedAt: Timestamp.fromDate(newRole.updatedAt),
      })

      await loadCustomRoles()
      return docRef.id
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Update custom role
  const updateCustomRole = async (roleId, updates) => {
    loading.value = true
    try {
      const roleRef = doc(db, 'roles', roleId)
      await updateDoc(roleRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
      })

      await loadCustomRoles()
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Delete custom role
  const deleteCustomRole = async (roleId) => {
    loading.value = true
    try {
      // Check if any employee is using this role
      const employeesWithRole = employees.value.filter(e => e.role === roleId)
      if (employeesWithRole.length > 0) {
        throw new Error(`Cannot delete role.${employeesWithRole.length} employees are still assigned to this role.`)
      }

      await deleteDoc(doc(db, 'roles', roleId))
      await loadCustomRoles()
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Get current company
  const currentCompany = computed(() => {
    if (!authStore.userProfile?.companyId) return null
    return companies.value.find((company) => company.id === authStore.userProfile.companyId)
  })

  // Get employees for current company (active only — used by roster, attendance, payroll, etc.)
  const companyEmployees = computed(() => {
    if (!authStore.userProfile?.companyId) return []
    return employees.value.filter(
      (employee) =>
        employee.companyId === authStore.userProfile.companyId && employee.isActive !== false,
    )
  })

  // Includes deactivated employees (employee management / detail pages)
  const allCompanyEmployees = computed(() => {
    if (!authStore.userProfile?.companyId) return []
    return employees.value.filter(
      (employee) => employee.companyId === authStore.userProfile.companyId,
    )
  })

  // Get branches for current company (deduplicated by ID, employee-scoped)
  const companyBranches = computed(() => {
    if (!authStore.userProfile?.companyId) return []
    const filtered = branches.value.filter(
      (branch) => branch.companyId === authStore.userProfile.companyId && branch.isActive !== false,
    )
    // Deduplicate by ID in case of any duplicates
    const uniqueBranches = new Map()
    filtered.forEach((branch) => {
      if (branch.id && !uniqueBranches.has(branch.id)) {
        uniqueBranches.set(branch.id, branch)
      }
    })
    return filterBranchesForUser(Array.from(uniqueBranches.values()), authStore.userProfile)
  })

  // Get employees by branch
  const getEmployeesByBranch = (branchId) => {
    if (!branchId) return companyEmployees.value
    return companyEmployees.value.filter((employee) => employee.branchId === branchId)
  }

  // Get current branch for user (if employee has branchId)
  const currentBranch = computed(() => {
    if (!authStore.userProfile?.branchId) return null
    return branches.value.find((branch) => branch.id === authStore.userProfile.branchId)
  })

  // Create a new company with admin account
  const createCompanyWithAdmin = async ({ company, admin }) => {
    loading.value = true
    try {
      // Create Firebase auth user for admin
      const { createUserWithEmailAndPassword } = await import('firebase/auth')
      const { auth } = await import('@/firebase/config')

      const userCredential = await createUserWithEmailAndPassword(auth, admin.email, admin.password)

      const adminUser = userCredential.user

      // Create company document
      const newCompany = {
        name: company.name,
        description: company.description,
        address: company.address,
        phone: company.phone,
        email: company.email,
        industry: company.industry,
        size: company.size,
        settings: {
          workHours: company.workHours || { start: '09:00', end: '17:00' },
          timezone: company.timezone || 'Asia/Kuala_Lumpur',
          allowRemoteWork: company.allowRemoteWork || false,
          requireGeofence: company.requireGeofence || true,
          faceRecognitionRequired: company.faceRecognitionRequired || true,
          geolocation: company.geolocation || {
            latitude: null,
            longitude: null,
            radius: 100, // meters
            enabled: false,
          },
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: adminUser.uid,
        adminId: adminUser.uid,
      }

      const companyRef = await addDoc(collection(db, 'companies'), {
        ...newCompany,
        createdAt: Timestamp.fromDate(newCompany.createdAt),
        updatedAt: Timestamp.fromDate(newCompany.updatedAt),
      })

      // Create admin user profile
      const adminProfile = {
        uid: adminUser.uid,
        email: adminUser.email,
        name: admin.name,
        role: 'admin',
        companyId: companyRef.id,
        phoneNumber: admin.phoneNumber,
        hasPDPAConsent: false,
        hasFaceRegistered: false,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      }

      await setDoc(doc(db, 'users', adminUser.uid), {
        ...adminProfile,
        createdAt: Timestamp.fromDate(adminProfile.createdAt),
        updatedAt: Timestamp.fromDate(adminProfile.updatedAt),
      })

      return {
        companyId: companyRef.id,
        adminId: adminUser.uid,
      }
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Create a new company (legacy method for existing authenticated users)
  const createCompany = async (companyData) => {
    loading.value = true
    try {
      // Check if user is authenticated
      if (!authStore.user || !authStore.user.uid) {
        throw new Error('User must be authenticated to create a company')
      }

      const newCompany = {
        name: companyData.name,
        description: companyData.description,
        address: companyData.address,
        phone: companyData.phone,
        email: companyData.email,
        industry: companyData.industry,
        size: companyData.size,
        settings: {
          workHours: companyData.workHours || { start: '09:00', end: '17:00' },
          timezone: companyData.timezone || 'Asia/Kuala_Lumpur',
          allowRemoteWork: companyData.allowRemoteWork || false,
          requireGeofence: companyData.requireGeofence || true,
          faceRecognitionRequired: companyData.faceRecognitionRequired || true,
          geolocation: companyData.geolocation || {
            latitude: null,
            longitude: null,
            radius: 100, // meters
            enabled: false,
          },
        },
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: authStore.user.uid,
      }

      const docRef = await addDoc(collection(db, 'companies'), {
        ...newCompany,
        createdAt: Timestamp.fromDate(newCompany.createdAt),
        updatedAt: Timestamp.fromDate(newCompany.updatedAt),
      })

      // Update user's companyId
      await authStore.updateProfile({ companyId: docRef.id })

      return docRef.id
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Update company
  const updateCompany = async (companyId, updates) => {
    loading.value = true
    try {
      const companyRef = doc(db, 'companies', companyId)
      await updateDoc(companyRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
      })

      // Refresh companies
      await loadCompanies()
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Load companies
  const loadCompanies = async () => {
    loading.value = true
    try {
      // Only load the company that the current user belongs to
      if (!authStore.userProfile?.companyId) {
        console.warn('User does not have a company ID')
        companies.value = []
        return
      }

      // Check if user has manager/admin role


      const companyRef = doc(db, 'companies', authStore.userProfile.companyId)
      const companyDoc = await getDoc(companyRef)

      if (companyDoc.exists()) {
        const companyData = companyDoc.data()
        companies.value = [
          {
            id: companyDoc.id,
            ...companyData,
            createdAt: companyData.createdAt?.toDate(),
            updatedAt: companyData.updatedAt?.toDate(),
          },
        ]
      } else {
        console.warn(`Company document not found for ID: ${authStore.userProfile.companyId} `)
        companies.value = []
        throw new Error(
          `Company document not found.Please contact support or recreate your company.Company ID: ${authStore.userProfile.companyId} `,
        )
      }
    } catch (error) {
      console.error('Error loading company:', error)
      companies.value = []
      throw error
    } finally {
      loading.value = false
    }
  }

  // Load employees for current company
  const loadEmployees = async () => {
    if (authStore.authLoading) return

    if (!authStore.userProfile?.companyId && !authStore.isSuperAdmin) {
      console.warn('Cannot load employees: No companyId in user profile')
      return
    }

    loading.value = true
    try {
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      let employeesQuery;
      if (authStore.isSuperAdmin && !authStore.userProfile?.companyId) {
        employeesQuery = query(collection(db, 'users'))
      } else if (businessOwnerSubCompanyId) {
        employeesQuery = query(
          collection(db, 'users'),
          where('companyId', '==', authStore.userProfile.companyId),
          where('subCompanyId', '==', businessOwnerSubCompanyId),
        )
      } else {
        employeesQuery = query(
          collection(db, 'users'),
          where('companyId', '==', authStore.userProfile.companyId),
        )
      }

      const employeesSnapshot = await getDocs(employeesQuery)
      const loadedEmployees = employeesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: toJsDate(doc.data().createdAt),
        updatedAt: toJsDate(doc.data().updatedAt),
        hireDate: toJsDate(doc.data().hireDate),
        contractPeriodStart: toJsDate(doc.data().contractPeriodStart),
        contractPeriodEnd: toJsDate(doc.data().contractPeriodEnd),
        confirmedDate: toJsDate(doc.data().confirmedDate),
      }))
      // Keep inactive employees so managers can show/delete them from the list UI
      employees.value = filterEmployeesForUser(loadedEmployees, authStore.userProfile)
    } catch (error) {
      console.error('Error loading employees:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const loadRosterDirectory = async () => {
    if (!authStore.userProfile?.companyId && !authStore.isSuperAdmin) return
    try {
      const data = await apiGet('/v1/attendance-actions/roster-directory')
      rosterDirectory.value = Array.isArray(data?.employees) ? data.employees : []
    } catch (error) {
      console.error('Error loading roster directory:', error)
      rosterDirectory.value = []
    }
  }

  const resolveManagedBranchIds = (role, requestedIds, actorProfile) => {
    if (!isBranchAssignableRole(role)) return []
    let managed = normalizeManagedBranchIds(requestedIds)
    if (isBranchScopedManager(actorProfile)) {
      const actorManaged = new Set(getManagedBranchIds(actorProfile))
      managed = managed.filter((id) => actorManaged.has(id))
    }
    return managed
  }

  const assertActorCanManageEmployeePayload = (actorProfile, payload, existingEmployee = null) => {
    if (!isBranchScopedManager(actorProfile)) return
    if (existingEmployee && !canManageEmployeeRecord(actorProfile, existingEmployee)) {
      throw new Error('You can only manage staff assigned to your branch.')
    }
    const branchId = payload?.branchId || null
    if (!canManageBranch(actorProfile, branchId)) {
      throw new Error('Staff must be assigned to a branch you manage.')
    }
  }

  // Create employee account
  const createEmployee = async (employeeData) => {
    loading.value = true
    let secondaryApp = null
    let secondaryAuth = null
    try {
      // Store admin's current auth state before creating employee
      const adminUser = authStore.user
      const adminProfile = authStore.userProfile

      const role = employeeData.role || 'employee'
      let branchId = employeeData.branchId || null
      const managedBranchIds = resolveManagedBranchIds(role, employeeData.managedBranchIds, adminProfile)
      if (isBranchAssignableRole(role) && managedBranchIds.length > 0 && !branchId) {
        branchId = managedBranchIds[0]
      }
      if (isBranchScopedManager(adminProfile) && !branchId) {
        const actorManaged = getManagedBranchIds(adminProfile)
        branchId = actorManaged[0] || null
      }
      assertActorCanManageEmployeePayload(adminProfile, { branchId })

      // Create Firebase auth user using a secondary app instance to avoid auto-login
      const { createUserWithEmailAndPassword, signOut, getAuth } = await import('firebase/auth')
      const { initializeApp, getApps, deleteApp } = await import('firebase/app')
      const { auth } = await import('@/firebase/config')

      // Firebase configuration from environment variables
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
        measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
      }

      // Create a secondary Firebase app instance
      const secondaryAppName = `secondary - ${Date.now()} `
      secondaryApp = initializeApp(firebaseConfig, secondaryAppName)
      secondaryAuth = getAuth(secondaryApp)

      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        employeeData.email,
        employeeData.tempPassword,
      )

      const newUser = userCredential.user
      let inferredSubCompanyId = employeeData.subCompanyId || adminProfile.subCompanyId || null
      if (!inferredSubCompanyId && branchId) {
        const selectedBranch = branches.value.find((branch) => branch.id === branchId)
        inferredSubCompanyId = selectedBranch?.subCompanyId || null
      }

      // Create user profile in Firestore
      const newEmployeeProfile = {
        uid: newUser.uid,
        email: newUser.email,
        name: employeeData.name,
        role,
        companyId: adminProfile.companyId,
        branchId: branchId || null,
        managedBranchIds,
        department: employeeData.department,
        position: employeeData.position,
        phoneNumber: employeeData.phoneNumber,
        employeeId: employeeData.employeeId,
        hireDate: employeeData.hireDate,
        salary: employeeData.salary,
        hourlyRate: employeeData.hourlyRate || null,
        monthlyRate: employeeData.monthlyRate || null,
        payBasis: employeeData.payBasis === 'hourly' ? 'hourly' : 'monthly',
        allowances: Number(employeeData.allowances || 0),
        individualKpi: Number(employeeData.individualKpi || 0),
        dailyAttendance: Number(employeeData.dailyAttendance || 0),
        enableEPF: employeeData.enableEPF !== false,
        enableSOCSO: employeeData.enableSOCSO !== false,
        enableEIS: employeeData.enableEIS !== false,
        enablePCB: employeeData.enablePCB !== false,
        epfNumber: employeeData.epfNumber || '',
        socsoNumber: employeeData.socsoNumber || '',
        bankName: employeeData.bankName || '',
        accountNumber: employeeData.accountNumber || '',
        maritalStatus: employeeData.maritalStatus || 'Single',
        gender: employeeData.gender || '',
        photoURL: employeeData.photoURL || null,
        currentAddress: employeeData.currentAddress || '',
        permanentAddress: employeeData.permanentAddress || '',
        emergencyContact: employeeData.emergencyContact || { name: '', relationship: '', phone: '' },
        employmentType: employeeData.employmentType || 'Full Time',
        employmentStatus: employeeData.employmentStatus || 'Probation',
        probationPeriod: employeeData.probationPeriod || '',
        contractPeriodStart: employeeData.contractPeriodStart || null,
        contractPeriodEnd: employeeData.contractPeriodEnd || null,
        confirmedDate: employeeData.confirmedDate || null,
        overtimeRate: employeeData.overtimeRate || null,
        publicHolidayEntitlement: employeeData.publicHolidayEntitlement || 0,
        yearlyBonus: employeeData.yearlyBonus || 0,
        leaveEntitled: employeeData.leaveEntitled || 0,
        hasPDPAConsent: false,
        hasFaceRegistered: false,
        isActive: true,
        createdBy: adminProfile.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
      }
      const scopedEmployeeProfile = applySubCompanyMetadata(newEmployeeProfile, adminProfile, inferredSubCompanyId)
      if ((scopedEmployeeProfile.role || '').toLowerCase() === 'business_owner' && !scopedEmployeeProfile.subCompanyId) {
        throw new Error('Business Owner must be assigned to a specific business')
      }

      const firestoreData = {
        ...scopedEmployeeProfile,
        createdAt: Timestamp.fromDate(scopedEmployeeProfile.createdAt),
        updatedAt: Timestamp.fromDate(scopedEmployeeProfile.updatedAt),
        hireDate: scopedEmployeeProfile.hireDate ? Timestamp.fromDate(new Date(scopedEmployeeProfile.hireDate)) : null,
      }

      if (scopedEmployeeProfile.contractPeriodStart) {
        firestoreData.contractPeriodStart = Timestamp.fromDate(new Date(scopedEmployeeProfile.contractPeriodStart))
      }
      if (scopedEmployeeProfile.contractPeriodEnd) {
        firestoreData.contractPeriodEnd = Timestamp.fromDate(new Date(scopedEmployeeProfile.contractPeriodEnd))
      }
      if (scopedEmployeeProfile.confirmedDate) {
        firestoreData.confirmedDate = Timestamp.fromDate(new Date(scopedEmployeeProfile.confirmedDate))
      }

      // Create the document in Firestore
      await setDoc(doc(db, 'users', newUser.uid), firestoreData)

      // Sign out from secondary auth and clean up
      if (secondaryAuth) {
        await signOut(secondaryAuth)
      }
      if (secondaryApp) {
        await deleteApp(secondaryApp)
      }

      // Send password reset email if requested
      if (employeeData.sendResetEmail) {
        try {
          await authStore.sendPasswordResetToEmployee({
            email: employeeData.email,
          })
        } catch (emailError) {
          console.warn('Failed to send reset email after employee creation:', emailError)
        }
      }

      // Refresh employees list
      await loadEmployees()

      return {
        userId: newUser.uid,
        email: newUser.email,
        tempPassword: employeeData.tempPassword,
      }
    } catch (error) {
      console.error('Error creating employee:', error)

      // Clean up any secondary app instances on error
      if (secondaryAuth) {
        try {
          await signOut(secondaryAuth)
        } catch (e) { console.warn('Secondary auth signout failed', e) }
      }
      if (secondaryApp) {
        try {
          await deleteApp(secondaryApp)
        } catch (e) { console.warn('Secondary app deletion failed', e) }
      }

      if (error.code === 'auth/email-already-in-use') {
        throw new Error('This email is already registered. Please use a different email.')
      }

      throw error // Re-throw other errors
    } finally {
      loading.value = false
    }
  }

  // Update employee
  const updateEmployee = async (employeeId, updates) => {
    loading.value = true
    try {
      if ((updates?.role || '').toLowerCase() === 'business_owner' && !updates?.subCompanyId) {
        throw new Error('Business Owner must be assigned to a specific business')
      }

      const adminProfile = authStore.userProfile
      const existingEmployee = employees.value.find((employee) => employee.id === employeeId || employee.uid === employeeId) || null
      const nextRole = updates?.role || existingEmployee?.role || 'employee'
      const payload = { ...updates }

      if (Object.prototype.hasOwnProperty.call(updates || {}, 'managedBranchIds')) {
        payload.managedBranchIds = resolveManagedBranchIds(nextRole, updates.managedBranchIds, adminProfile)
      } else if (Object.prototype.hasOwnProperty.call(updates || {}, 'role') && !isBranchAssignableRole(nextRole)) {
        payload.managedBranchIds = []
      }
      if (isBranchAssignableRole(nextRole) && Array.isArray(payload.managedBranchIds) && payload.managedBranchIds.length > 0 && !payload.branchId && !existingEmployee?.branchId) {
        payload.branchId = payload.managedBranchIds[0]
      }

      assertActorCanManageEmployeePayload(adminProfile, {
        branchId: payload.branchId !== undefined ? payload.branchId : existingEmployee?.branchId,
      }, existingEmployee)

      const employeeRef = doc(db, 'users', employeeId)
      await updateDoc(employeeRef, {
        ...payload,
        updatedAt: Timestamp.fromDate(new Date()),
      })

      // Refresh employees list
      await loadEmployees()
    } catch (error) {
      throw error
    } finally {
      loading.value = false
    }
  }

  // Deactivate employee
  const deactivateEmployee = async (employeeId) => {
    await updateEmployee(employeeId, { isActive: false })
  }

  // Activate employee
  const activateEmployee = async (employeeId) => {
    await updateEmployee(employeeId, { isActive: true })
  }

  // Generate temporary password
  const generateTempPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
    let password = ''
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return password
  }

  // Reset employee password
  const resetEmployeePassword = async (employeeId) => {
    const tempPassword = generateTempPassword()

    // Call facial recognition microservice (which has admin privileges) to reset password
    // We use this service because it already has Firebase Admin SDK initialized
    try {
      // Lazy load axios
      const axios = (await import('axios')).default
      const FACE_API_URL = import.meta.env.VITE_FACE_API_URL || 'http://localhost:8001'

      await axios.post(`${FACE_API_URL}/admin/reset-password`, {
        employee_id: employeeId,
        new_password: tempPassword
      })

      return tempPassword
    } catch (error) {
      console.error('Failed to reset password via admin API:', error)
      throw new Error('Failed to reset password. Please try again.')
    }
  }

  // Delete employee
  const deleteEmployee = async (employeeId) => {
    try {
      // Lazy load axios
      const axios = (await import('axios')).default
      const FACE_API_URL = import.meta.env.VITE_FACE_API_URL || 'http://localhost:8001'

      // Find company ID
      const employee = employees.value.find((e) => e.uid === employeeId || e.id === employeeId)
      const companyId = employee?.companyId || authStore.userProfile?.companyId

      if (!companyId) {
        throw new Error('Company ID not found')
      }

      await axios.post(`${FACE_API_URL}/admin/delete-employee`, {
        employee_id: employeeId,
        company_id: companyId
      })

      // Refresh employees list
      await loadEmployees()

      return true
    } catch (error) {
      console.error('Failed to delete employee via admin API:', error)
      if (error.response?.data?.detail?.message) {
        throw new Error(error.response.data.detail.message)
      }
      throw new Error('Failed to delete employee. Please try again.')
    }
  }

  // Update company geolocation settings
  const updateGeolocationSettings = async (geolocationData) => {
    if (!authStore.userProfile?.companyId) {
      throw new Error(
        'Company ID not found. Please ensure you are logged in and have a company assigned.',
      )
    }

    loading.value = true
    try {
      const companyRef = doc(db, 'companies', authStore.userProfile.companyId)

      // Check if company document exists
      const companyDoc = await getDoc(companyRef)
      if (!companyDoc.exists()) {
        throw new Error(
          `Company document not found.Please contact support or recreate your company.Company ID: ${authStore.userProfile.companyId} `,
        )
      }

      await updateDoc(companyRef, {
        'settings.geolocation': geolocationData,
        updatedAt: Timestamp.fromDate(new Date()),
      })

      // Refresh companies
      await loadCompanies()
    } catch (error) {
      console.error('Error updating geolocation settings:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Update company working hours
  const updateWorkingHours = async (workHoursData) => {
    if (!authStore.userProfile?.companyId) {
      throw new Error(
        'Company ID not found. Please ensure you are logged in and have a company assigned.',
      )
    }

    loading.value = true
    try {
      const companyRef = doc(db, 'companies', authStore.userProfile.companyId)

      // Check if company document exists
      const companyDoc = await getDoc(companyRef)
      if (!companyDoc.exists()) {
        throw new Error(
          `Company document not found.Please contact support or recreate your company.Company ID: ${authStore.userProfile.companyId} `,
        )
      }

      await updateDoc(companyRef, {
        'settings.workHours': workHoursData,
        updatedAt: Timestamp.fromDate(new Date()),
      })

      // Refresh companies
      await loadCompanies()
    } catch (error) {
      console.error('Error updating working hours:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Update company settings (general function for any settings)
  const updateCompanySettings = async (settingsData) => {
    if (!authStore.userProfile?.companyId) {
      throw new Error(
        'Company ID not found. Please ensure you are logged in and have a company assigned.',
      )
    }

    loading.value = true
    console.log('Updating company settings:', settingsData)
    try {
      const companyRef = doc(db, 'companies', authStore.userProfile.companyId)

      // Check if company document exists
      const companyDoc = await getDoc(companyRef)
      if (!companyDoc.exists()) {
        throw new Error(
          `Company document not found.Please contact support or recreate your company.Company ID: ${authStore.userProfile.companyId} `,
        )
      }

      // Get existing settings and merge
      const existingData = companyDoc.data()
      const existingSettings = existingData.settings || {}
      const updatedSettings = { ...existingSettings, ...settingsData }

      await updateDoc(companyRef, {
        settings: updatedSettings,
        updatedAt: Timestamp.fromDate(new Date()),
      })

      // Refresh companies
      await loadCompanies()
    } catch (error) {
      console.error('Error updating company settings:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Check if current time is within working hours (checks branch first, then company)
  const isWithinWorkingHours = () => {
    // Check branch working hours first if user is assigned to a branch
    const userBranchId = authStore.userProfile?.branchId
    const branch = userBranchId
      ? branches.value.find((b) => b.id === userBranchId)
      : currentBranch.value

    if (branch?.settings?.workHours) {
      const now = new Date()
      const currentTime = now.toTimeString().slice(0, 5) // HH:MM format
      const { start, end } = branch.settings.workHours

      // Handle overnight shifts (e.g., 21:00 to 05:00)
      if (start > end) {
        // Overnight shift: time is valid if >= start OR <= end
        return currentTime >= start || currentTime <= end
      } else {
        // Normal shift: time is valid if >= start AND <= end
        return currentTime >= start && currentTime <= end
      }
    }

    // Fallback to company-level working hours
    if (!currentCompany.value?.settings?.workHours) return true

    const now = new Date()
    const currentTime = now.toTimeString().slice(0, 5) // HH:MM format
    const { start, end } = currentCompany.value.settings.workHours

    // Handle overnight shifts (e.g., 21:00 to 05:00)
    if (start > end) {
      // Overnight shift: time is valid if >= start OR <= end
      return currentTime >= start || currentTime <= end
    } else {
      // Normal shift: time is valid if >= start AND <= end
      return currentTime >= start && currentTime <= end
    }
  }

  // Find a branch that the user is currently within radius of
  const findNearbyBranch = (userLat, userLng, accuracy = 0) => {
    if (!companyBranches.value.length) return null

    for (const branch of companyBranches.value) {
      if (branch.settings?.geolocation?.enabled) {
        const { latitude, longitude, radius } = branch.settings.geolocation
        if (latitude && longitude) {
          const distance = calculateDistance(userLat, userLng, latitude, longitude)
          const accuracyBuffer = Math.max(0, accuracy / 2)
          const adjustedDistance = Math.max(0, distance - accuracyBuffer)
          
          if (adjustedDistance <= (radius || 100)) {
            return branch
          }
        }
      }
    }
    return null
  }

  // Check if location is within allowed radius (checks branch first, then company)
  const isWithinLocationRadius = (userLat, userLng, branchId = null, accuracy = 0) => {
    // If no branchId provided, check if user is within ANY branch radius first
    // This supports "Company-wide" employees who can clock in at any branch
    if (!branchId) {
      const nearbyBranch = findNearbyBranch(userLat, userLng, accuracy)
      if (nearbyBranch) return true
    }

    // If user has a branch, check branch location
    const branch = branchId ? branches.value.find((b) => b.id === branchId) : currentBranch.value

    if (branch?.settings?.geolocation?.enabled) {
      const { latitude, longitude, radius } = branch.settings.geolocation
      if (latitude && longitude) {
        const distance = calculateDistance(userLat, userLng, latitude, longitude)
        
        // Use accuracy buffer: distance - (accuracy / 2) <= radius
        // This accounts for GPS drift in indoor or multistory locations
        const accuracyBuffer = Math.max(0, accuracy / 2)
        const adjustedDistance = Math.max(0, distance - accuracyBuffer)
        
        return adjustedDistance <= (radius || 100)
      } else {
        // Branch geolocation enabled but coordinates missing - should block
        return false
      }
    }

    // Fallback to company-level geolocation only if branch geolocation is not enabled
    if (!currentCompany.value?.settings?.geolocation?.enabled) return true

    const { latitude, longitude, radius } = currentCompany.value.settings.geolocation
    if (!latitude || !longitude) return true

    const distance = calculateDistance(userLat, userLng, latitude, longitude)
    const accuracyBuffer = Math.max(0, accuracy / 2)
    const adjustedDistance = Math.max(0, distance - accuracyBuffer)
    
    return adjustedDistance <= (radius || 100)
  }

  // Get geolocation settings (prioritizes branch over company)
  const getGeolocationSettings = (branchId = null) => {
    const branch = branchId ? branches.value.find((b) => b.id === branchId) : currentBranch.value

    if (branch?.settings?.geolocation) {
      return branch.settings.geolocation
    }

    return (
      currentCompany.value?.settings?.geolocation || {
        latitude: null,
        longitude: null,
        radius: 100,
        enabled: false,
      }
    )
  }

  /**
   * Resolve whether a user can clock in at their GPS position.
   * Checks preferred branch (roster/profile) first, then any company branch.
   */
  const resolveAttendanceBranch = (userLat, userLng, preferredBranchId = null, accuracy = 0) => {
    if (preferredBranchId && isWithinLocationRadius(userLat, userLng, preferredBranchId, accuracy)) {
      const branch = getBranchById(preferredBranchId)
      return {
        isValid: true,
        branchId: preferredBranchId,
        branchName: branch?.name || 'workplace',
        usedAlternateBranch: false,
      }
    }

    const nearbyBranch = findNearbyBranch(userLat, userLng, accuracy)
    if (nearbyBranch) {
      return {
        isValid: true,
        branchId: nearbyBranch.id,
        branchName: nearbyBranch.name,
        usedAlternateBranch: !!(preferredBranchId && nearbyBranch.id !== preferredBranchId),
      }
    }

    if (!preferredBranchId && isWithinLocationRadius(userLat, userLng, null, accuracy)) {
      return {
        isValid: true,
        branchId: null,
        branchName: 'workplace',
        usedAlternateBranch: false,
      }
    }

    const preferredBranch = preferredBranchId ? getBranchById(preferredBranchId) : null
    const settings = getGeolocationSettings(preferredBranchId)
    return {
      isValid: false,
      branchId: preferredBranchId,
      branchName: preferredBranch?.name || 'workplace',
      radius: settings?.radius || 100,
      usedAlternateBranch: false,
    }
  }

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3 // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180
    const φ2 = (lat2 * Math.PI) / 180
    const Δφ = ((lat2 - lat1) * Math.PI) / 180
    const Δλ = ((lon2 - lon1) * Math.PI) / 180

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return R * c // Distance in meters
  }

  // Load branches for current company
  const loadBranches = async () => {
    if (authStore.authLoading) return

    if (!authStore.userProfile?.companyId && !authStore.isSuperAdmin) {
      console.warn('Cannot load branches: No companyId in user profile')
      return
    }

    loading.value = true
    try {
      const businessOwnerSubCompanyId = getBusinessOwnerSubCompanyId()
      let branchesQuery;
      if (authStore.isSuperAdmin && !authStore.userProfile?.companyId) {
        branchesQuery = query(collection(db, 'branches'))
      } else if (businessOwnerSubCompanyId) {
        branchesQuery = query(
          collection(db, 'branches'),
          where('companyId', '==', authStore.userProfile.companyId),
          where('subCompanyId', '==', businessOwnerSubCompanyId),
        )
      } else {
        branchesQuery = query(
          collection(db, 'branches'),
          where('companyId', '==', authStore.userProfile.companyId),
        )
      }

      const branchesSnapshot = await getDocs(branchesQuery)

      // Map and deduplicate branches by document ID
      const branchesMap = new Map()
      branchesSnapshot.docs.forEach((doc) => {
        if (!branchesMap.has(doc.id)) {
          const data = doc.data()
          branchesMap.set(doc.id, {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          })
        }
      })

      branches.value = Array.from(branchesMap.values())
    } catch (error) {
      console.error('Error loading branches:', error)
      // If it's a permission error, log more details
      if (error.code === 'permission-denied') {
        console.error('Permission denied loading branches. User:', {
          uid: authStore.userProfile?.uid,
          companyId: authStore.userProfile?.companyId,
          role: authStore.userProfile?.role,
        })
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  // Create branch
  const createBranch = async (branchData) => {
    if (!authStore.userProfile?.companyId) {
      throw new Error(
        'Company ID not found. Please ensure you are logged in and have a company assigned.',
      )
    }

    loading.value = true
    try {
      const newBranch = applySubCompanyMetadata({
        companyId: authStore.userProfile.companyId,
        name: branchData.name,
        address: branchData.address || '',
        phone: branchData.phone || '',
        email: branchData.email || '',
        description: branchData.description || '',
        settings: {
          workHours: branchData.workHours ||
            currentCompany.value?.settings?.workHours || { start: '09:00', end: '17:00' },
          timezone:
            branchData.timezone || currentCompany.value?.settings?.timezone || 'Asia/Kuala_Lumpur',
          allowRemoteWork:
            branchData.allowRemoteWork ?? currentCompany.value?.settings?.allowRemoteWork ?? false,
          requireGeofence:
            branchData.requireGeofence ?? currentCompany.value?.settings?.requireGeofence ?? true,
          faceRecognitionRequired:
            branchData.faceRecognitionRequired ??
            currentCompany.value?.settings?.faceRecognitionRequired ??
            true,
          geolocation: branchData.geolocation || {
            latitude: null,
            longitude: null,
            radius: 100, // meters
            enabled: false,
          },
          staffing: branchData.staffing || {
            minStaff: currentCompany.value?.settings?.roster?.minPersonsPerDay ?? 2,
            maxStaff: currentCompany.value?.settings?.roster?.maxPersonsPerDay ?? 5,
          },
          salesIntegration: {
            externalLocationId: branchData.salesIntegration?.externalLocationId || '',
          },
        },
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: authStore.userProfile.uid,
      }, authStore.userProfile, branchData.subCompanyId || null)

      const branchRef = await addDoc(collection(db, 'branches'), {
        ...newBranch,
        createdAt: Timestamp.fromDate(newBranch.createdAt),
        updatedAt: Timestamp.fromDate(newBranch.updatedAt),
      })

      // Refresh branches list
      await loadBranches()

      return branchRef.id
    } catch (error) {
      console.error('Error creating branch:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Update branch
  const updateBranch = async (branchId, updates) => {
    loading.value = true
    try {
      const branchRef = doc(db, 'branches', branchId)

      // If updating settings, merge with existing settings
      if (updates.settings) {
        const branchDoc = await getDoc(branchRef)
        if (branchDoc.exists()) {
          const existingSettings = branchDoc.data().settings || {}
          updates.settings = { ...existingSettings, ...updates.settings }
        }
      }

      await updateDoc(branchRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
      })

      // Refresh branches list
      await loadBranches()
    } catch (error) {
      console.error('Error updating branch:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // Delete branch (soft delete by setting isActive to false)
  const deleteBranch = async (branchId) => {
    await updateBranch(branchId, { isActive: false })
  }

  // Reactivate branch
  const reactivateBranch = async (branchId) => {
    await updateBranch(branchId, { isActive: true })
  }

  // Get branch by ID
  const getBranchById = (branchId) => {
    return branches.value.find((branch) => branch.id === branchId)
  }

  // Clear invalid company ID from user profile
  const clearInvalidCompanyId = async () => {
    if (!authStore.userProfile?.companyId) return

    try {
      await authStore.updateProfile({ companyId: null })
      console.log('Cleared invalid company ID from user profile')
    } catch (error) {
      console.error('Error clearing company ID:', error)
      throw error
    }
  }

  return {
    companies,
    employees,
    rosterDirectory,
    branches,
    customRoles,
    rolesLoaded,
    loading,
    currentCompany,
    companyEmployees,
    allCompanyEmployees,
    companyBranches,
    currentBranch,
    getEmployeesByBranch,
    createCompanyWithAdmin,
    createCompany,
    updateCompany,
    loadCompanies,
    loadEmployees,
    loadRosterDirectory,
    loadBranches,
    loadCustomRoles,
    createCustomRole,
    updateCustomRole,
    deleteCustomRole,
    createEmployee,
    updateEmployee,
    deactivateEmployee,
    activateEmployee,
    deleteEmployee,
    resetEmployeePassword,
    updateCompanySettings,
    updateGeolocationSettings,
    updateWorkingHours,
    isWithinWorkingHours,
    isWithinLocationRadius,
    findNearbyBranch,
    resolveAttendanceBranch,
    getGeolocationSettings,
    calculateDistance,
    createBranch,
    updateBranch,
    deleteBranch,
    reactivateBranch,
    getBranchById,
    clearInvalidCompanyId,
  }
})
